import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Shield, KeyRound, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import RotatingBackground from '@/components/RotatingBackground';

import { API_URL } from '@/config/api';

// BOH Roles that are allowed to access the admin portal
const BOH_ROLES = [
  'super_admin',
  'manager',
  'operations_manager',
  'kitchen',
  'fulfilment',
  'customer_support',
  'finance',
  'driver',
  'admin', // Legacy
  'staff'  // Legacy
];

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Check for success message from invite acceptance
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      if (location.state.email) {
        setFormData(prev => ({ ...prev, email: location.state.email }));
      }
      // Clear the state to prevent showing message on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Password change modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState(null);
  const [passwordChange, setPasswordChange] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      const data = await response.json();

      // Check if 2FA is required (password auth succeeded but 2FA needed)
      if (data.requires_2fa) {
        navigate('/admin/2fa-verify', { state: { user_id: data.user_id }, replace: true });
        return;
      }

      // Check if user has a BOH role
      if (!BOH_ROLES.includes(data.user.role)) {
        throw new Error('BOH access required');
      }

      // Check if password change is required
      if (data.must_change_password) {
        setPendingLoginData(data);
        setShowPasswordModal(true);
        return;
      }

      // Normal login flow
      login(data.access_token, data.user);
      toast.success('Welcome to BOH Portal');
      navigate('/admin', { replace: true });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordChange.newPassword !== passwordChange.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwordChange.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setChangingPassword(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${pendingLoginData.access_token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          current_password: formData.password,
          new_password: passwordChange.newPassword
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Password change failed');
      }

      // Password changed successfully, now complete login
      login(pendingLoginData.access_token, pendingLoginData.user);
      toast.success('Password changed successfully. Welcome to BOH Portal!');
      setShowPasswordModal(false);
      navigate('/admin', { replace: true });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setChangingPassword(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setForgotPasswordLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotPasswordEmail.trim(), user_type: 'boh' })
      });

      // Always show success to prevent email enumeration
      setForgotPasswordSent(true);
    } catch (error) {
      setForgotPasswordSent(true);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen flex" data-testid="admin-login">
        {/* Left Side - Rotating Background */}
        <div className="hidden lg:flex lg:w-1/2 relative">
          <RotatingBackground 
            interval={15000} 
            overlay={true} 
            overlayColor="bg-secondary-900/70"
            className="absolute inset-0"
            variant="admin"
          >
            <div className="h-full flex items-center justify-center p-12">
              <div className="text-center">
                <img 
                  src="https://gc3h.co.za/wp-content/uploads/2022/02/gallagher-convention-centre-catering.png"
                  alt="GC3H"
                  className="h-28 w-auto mx-auto mb-8"
                />
                <div className="flex items-center justify-center gap-2 text-primary mb-4">
                  <Shield className="h-6 w-6" />
                  <span className="text-lg font-semibold uppercase tracking-wider">Back-of-House Portal</span>
                </div>
                <p className="text-white/70 text-lg max-w-sm mx-auto">
                  Manage orders, menus, kitchen operations and more from one central dashboard.
                </p>
              </div>
            </div>
          </RotatingBackground>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center bg-secondary-900 lg:bg-white p-4">
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <img 
                src="https://gc3h.co.za/wp-content/uploads/2022/02/gallagher-convention-centre-catering.png"
                alt="GC3H"
                className="h-20 w-auto mx-auto mb-4"
              />
              <div className="flex items-center justify-center gap-2 text-primary">
                <Shield className="h-5 w-5" />
                <span className="text-sm font-semibold uppercase tracking-wider">Back-of-House Portal</span>
              </div>
            </div>

            {/* Login Form Card */}
            <div className="bg-white p-8 rounded-sm lg:shadow-none lg:p-0">
              <h1 className="font-heading text-2xl font-semibold text-secondary-900 mb-6 text-center lg:text-left">Admin Login</h1>

            {/* Success Message from Invite Acceptance */}
            {successMessage && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-green-800 font-medium">{successMessage}</p>
                  {formData.email && (
                    <p className="text-xs text-green-600 mt-1">Email: {formData.email}</p>
                  )}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-secondary-900">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="admin@gc3h.co.za"
                    className="pl-10"
                    required
                    data-testid="admin-email"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-secondary-900">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder="Enter your password"
                    className="pl-10 pr-10"
                    required
                    data-testid="admin-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <button 
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-primary hover:underline"
                  data-testid="admin-forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-white hover:bg-primary-600 py-6 uppercase tracking-wider"
                data-testid="admin-login-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-muted-foreground">Or</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
              className="w-full py-6"
              data-testid="admin-google-login"
            >
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Forced Password Change Modal */}
      <Dialog open={showPasswordModal} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()} aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              Password Change Required
            </DialogTitle>
            <DialogDescription>
              For security reasons, you must change your password before accessing the system.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePasswordChange} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordChange.newPassword}
                onChange={(e) => setPasswordChange(prev => ({ ...prev, newPassword: e.target.value }))}
                placeholder="Enter new password (min. 8 characters)"
                className="mt-1"
                required
                minLength={8}
                data-testid="new-password"
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordChange.confirmPassword}
                onChange={(e) => setPasswordChange(prev => ({ ...prev, confirmPassword: e.target.value }))}
                placeholder="Confirm new password"
                className="mt-1"
                required
                minLength={8}
                data-testid="confirm-password"
              />
            </div>
            <Button
              type="submit"
              disabled={changingPassword}
              className="w-full bg-primary text-white hover:bg-primary-600"
              data-testid="change-password-btn"
            >
              {changingPassword ? 'Changing Password...' : 'Change Password & Continue'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={(open) => {
        setShowForgotPassword(open);
        if (!open) {
          setForgotPasswordEmail('');
          setForgotPasswordSent(false);
        }
      }}>
        <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              {!forgotPasswordSent 
                ? "Enter your email address and we'll send you a link to reset your password."
                : "Check your email for a password reset link."
              }
            </DialogDescription>
          </DialogHeader>
          
          {!forgotPasswordSent ? (
            <form onSubmit={handleForgotPassword} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="forgotEmail">Email Address</Label>
                <Input
                  id="forgotEmail"
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="mt-1"
                  required
                />
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForgotPassword(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={forgotPasswordLoading}
                  className="flex-1 bg-primary text-white hover:bg-primary-600"
                >
                  {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </div>
            </form>
          ) : (
            <div className="text-center py-6">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <p className="text-sm text-muted-foreground mb-4">
                If an account exists with <strong>{forgotPasswordEmail}</strong>, a password reset link has been sent.
              </p>
              <p className="text-xs text-muted-foreground mb-6">
                The link will expire in 1 hour.
              </p>
              <Button
                onClick={() => setShowForgotPassword(false)}
                variant="outline"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
